import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CircleDollarSign,
  Fuel,
  Gauge,
  Navigation,
  PencilLine,
  Route,
} from "lucide-react";
import { PERMISSIONS } from "../../Permissions";
import fuelService from "../../services/fuel.service";
import {
  formatDistance,
  formatFuelCost,
  formatFuelRate,
  formatVehicleAverage,
} from "../../utils/travelFormat";
import { t } from "../../locales/translate";
import EditFuelRateModal from "./EditFuelRateModal";
import EditVehicleAverageModal from "./EditVehicleAverageModal";
import SingleSelect from "../designSystem/SingleSelect";
import { getSubOrdinates } from "../../actions/userActions/tasks/taskAction";
import "./TravelSummary.css";

const TRAVEL_PERMISSIONS = [
  PERMISSIONS.CAN_VIEW_EMPLOYEE_TASK_STATS,
  PERMISSIONS.CAN_VIEW_FUEL_PRICE,
  PERMISSIONS.CAN_VIEW_VEHICLE_AVERAGE,
  PERMISSIONS.CAN_ADD_FUEL_PRICE,
  PERMISSIONS.CAN_ADD_VEHICLE_AVERAGE,
];

const sortSubordinates = (list) =>
  [...list].sort((a, b) => {
    const firstNameComparison = a.firstName.localeCompare(b.firstName);
    if (firstNameComparison !== 0) return firstNameComparison;
    return a.lastName.localeCompare(b.lastName);
  });

const mapEmployeeOptions = (employees) =>
  sortSubordinates(employees).map((employee) => ({
    value: employee.id,
    label: `${employee.firstName} ${employee.lastName}`,
    subtext: employee.designationName || employee.designationShortName || "",
  }));

const TravelSummary = ({ refreshKey, employeeId: loggedInEmployeeId }) => {
  const dispatch = useDispatch();
  const permissions = JSON.parse(localStorage.getItem("permissionsList")) || [];

  const subordinatesByLevel = useSelector(
    (state) => state.employees_reducer.employeesByLevel
  );
  const subordinates = useSelector((state) => state.tasks_reducer.subOrdinates);
  const isSubordinatesLoading = useSelector(
    (state) => state.tasks_reducer.isSubordinatesLoading
  );

  const [taskStats, setTaskStats] = useState(null);
  const [fuelPrice, setFuelPrice] = useState(null);
  const [vehicleAverage, setVehicleAverage] = useState(null);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showAverageModal, setShowAverageModal] = useState(false);
  const [viewSubordinates, setViewSubordinates] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
const [isStatsLoading, setIsStatsLoading] = useState(false);
  const canViewStats = permissions.includes(
    PERMISSIONS.CAN_VIEW_EMPLOYEE_TASK_STATS
  );
  const canViewFuelPrice = permissions.includes(PERMISSIONS.CAN_VIEW_FUEL_PRICE);
  const canAddFuelPrice = permissions.includes(PERMISSIONS.CAN_ADD_FUEL_PRICE);
  const canViewVehicleAverage = permissions.includes(
    PERMISSIONS.CAN_VIEW_VEHICLE_AVERAGE
  );
  const canAddVehicleAverage = permissions.includes(
    PERMISSIONS.CAN_ADD_VEHICLE_AVERAGE
  );
  const canViewSubOrdinate = permissions.includes(PERMISSIONS.CAN_VIEW_SUBORDINATE);

  const hasAnyPermission = TRAVEL_PERMISSIONS.some((p) =>
    permissions.includes(p)
  );

  const statsEmployeeId =
    viewSubordinates && selectedEmployee ? selectedEmployee : loggedInEmployeeId;

  useEffect(() => {
    if (viewSubordinates && loggedInEmployeeId) {
      dispatch(getSubOrdinates(loggedInEmployeeId, -1, 0, ""));
    }
  }, [viewSubordinates, loggedInEmployeeId, dispatch]);

  const employeeOptions = useMemo(() => {
    if (
      subordinates &&
      subordinates !== "isLoading" &&
      subordinates.length > 0
    ) {
      return mapEmployeeOptions(subordinates);
    }

    if (
      subordinatesByLevel &&
      subordinatesByLevel !== "isLoading" &&
      subordinatesByLevel.length > 0
    ) {
      return mapEmployeeOptions(subordinatesByLevel);
    }

    return [];
  }, [subordinates, subordinatesByLevel]);

  const fetchData = useCallback(async () => {
    setIsStatsLoading(true);
    const requests = [];

    if (canViewStats && statsEmployeeId) {
      requests.push(
        fuelService
          .getTaskStats(statsEmployeeId)
          .then((res) => {
            if (res.data?.status && res.data?.hasResult) {
              setTaskStats(res.data.result[0]);
            }
          })
          .catch(() => setTaskStats(null))
      );
    }

    if (canViewFuelPrice) {
      requests.push(
        fuelService
          .getLatestFuelPrice()
          .then((res) => {
            if (res.data?.status && res.data?.hasResult) {
              setFuelPrice(res.data.result[0]);
            }
          })
          .catch(() => setFuelPrice(null))
      );
    }

    if (canViewVehicleAverage) {
      requests.push(
        fuelService
          .getLatestVehicleAverage()
          .then((res) => {
            if (res.data?.status && res.data?.hasResult) {
              setVehicleAverage(res.data.result[0]);
            }
          })
          .catch(() => setVehicleAverage(null))
      );
    }

    await Promise.all(requests);
    setIsStatsLoading(false);
  }, [
    canViewStats,
    canViewFuelPrice,
    canViewVehicleAverage,
    statsEmployeeId,
  ]);

  useEffect(() => {
    if (hasAnyPermission) {
      fetchData();
    }
  }, [fetchData, hasAnyPermission, refreshKey]);

  const handleViewSubordinatesToggle = () => {
    const next = !viewSubordinates;
    setViewSubordinates(next);
    if (!next) {
      setSelectedEmployee(null);
    }
  };

  const handleEmployeeChange = (option) => {
    setSelectedEmployee(option?.value ?? null);
  };

  const cards = useMemo(() => {
    const items = [];

    if (canViewStats) {
      items.push({
        key: "distance",
        label: t("totalDistance"),
        value: formatDistance(taskStats?.totalDistance),
        icon: <Route color="#22AD5C" size={18} />,
      });
      items.push({
        key: "fuelCost",
        label: t("totalFuelCost"),
        value: formatFuelCost(taskStats?.totalFuelCost),
        icon: <Fuel color="#2D68F8" size={18} />,
      });
    }

    if (canViewFuelPrice) {
      items.push({
        key: "fuelRate",
        label: t("fuelRate"),
        value: formatFuelRate(fuelPrice?.price),
        icon: <CircleDollarSign color="#F27430" size={18} />,
        editable: canAddFuelPrice,
        onEdit: () => setShowFuelModal(true),
      });
    }

    if (canViewVehicleAverage) {
      items.push({
        key: "vehicleAverage",
        label: t("vehicleAverage"),
        value: formatVehicleAverage(vehicleAverage?.average),
        icon: <Gauge color="#FBBF24" size={18} />,
        editable: canAddVehicleAverage,
        onEdit: () => setShowAverageModal(true),
      });
    }

    return items;
  }, [
    canViewStats,
    canViewFuelPrice,
    canAddFuelPrice,
    canViewVehicleAverage,
    canAddVehicleAverage,
    taskStats,
    fuelPrice,
    vehicleAverage,
  ]);

  if (!hasAnyPermission || cards.length === 0) {
    return null;
  }

  const showSubordinateControls = canViewSubOrdinate && canViewStats;

  return (
    <>
      <div className="travel-summary-container">
        <div className="travel-summary-card">
          <div className="travel-summary-header">
            <div className="travel-summary-header-left">
              <Navigation color="#1e293b" size={20} />
              <h3 className="travel-summary-title">{t("travelSummary")}</h3>
            </div>

            {showSubordinateControls && (
                            <div className="travel-summary-header-controls">
                <div
                  className="travel-summary-employee-select"
                  style={{
                    visibility: viewSubordinates ? "visible" : "hidden",
                    opacity: viewSubordinates ? 1 : 0,
                    pointerEvents: viewSubordinates ? "auto" : "none",
                    transition: "opacity 0.2s, visibility 0.2s",
                  }}
                >
                  <SingleSelect
                    isSearchable
                    showClear
                    value={selectedEmployee}
                    placeholder={t("selectEmployee")}
                    options={employeeOptions}
                    onChange={handleEmployeeChange}
                    isLoading={isSubordinatesLoading}
                  />
                </div>

                <div className="travel-summary-toggle-row">
                  <span className="travel-summary-toggle-label">
                    {t("viewSubordinates")}
                  </span>
                  <div
                    className={`toggle-switch ${
                      viewSubordinates ? "toggle-on" : "toggle-off"
                    }`}
                    onClick={handleViewSubordinatesToggle}
                    role="switch"
                    aria-checked={viewSubordinates}
                  >
                    <div className="toggle-button" />
                  </div>
                </div>
              </div>
            )}
          </div>

                    <div className="travel-summary-stats">
            {isStatsLoading ? (
              Array.from({ length: Math.max(cards.length, 1) }).map((_, i) => (
                <div key={i} className="travel-stat-card skeleton-pulse">
                  <div className="travel-stat-card-header">
                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#e2e8f0', display: 'inline-block' }} />
                    <span style={{ width: '60%', height: 14, borderRadius: 4, background: '#e2e8f0', display: 'inline-block', marginLeft: 8 }} />
                  </div>
                  <div className="travel-stat-value-row" style={{ marginTop: 12 }}>
                    <span style={{ width: '45%', height: 22, borderRadius: 4, background: '#e2e8f0', display: 'inline-block' }} />
                  </div>
                </div>
              ))
            ) : (
              cards.map((card) => (
                <div key={card.key} className="travel-stat-card">
                  {/* keep existing card JSX exactly as-is */}
                  <div className="travel-stat-card-header">
                    <span className="travel-stat-icon">{card.icon}</span>
                    <span className="travel-stat-label">{card.label}</span>
                  </div>
                  <div className="travel-stat-value-row">
                    <span className="travel-stat-value">{card.value}</span>
                    {card.editable && (
                      <button
                        type="button"
                        className="travel-stat-edit"
                        onClick={card.onEdit}
                      >
                        <PencilLine size={14} />
                        {t("edit")}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showFuelModal && (
        <EditFuelRateModal
          editData={fuelPrice}
          onClose={() => setShowFuelModal(false)}
          onSave={(data) => {
            setFuelPrice(data);
            setShowFuelModal(false);
          }}
        />
      )}

      {showAverageModal && (
        <EditVehicleAverageModal
          editData={vehicleAverage}
          onClose={() => setShowAverageModal(false)}
          onSave={(data) => {
            setVehicleAverage(data);
            setShowAverageModal(false);
          }}
        />
      )}
    </>
  );
};

export default TravelSummary;
